import { DBSchema, IDBPDatabase, openDB } from 'idb'

import { GameEvent, User } from '@/types'

import { MessageAction, OutcomingMessage } from '../main-frame/message-types'

const IDB_NAME = 'pg_game_safe_storage'
const IDB_STORE = 'game_saved_state'

interface IDBStorageRecord {
    value: string
    gameKey: string
    gameId: string
}
interface SafeStorageGameDBSchema extends DBSchema {
    [IDB_STORE]: {
        key: [string, string]
        value: IDBStorageRecord
        indexes: { gameId: string; uniqueKeysForGame: [string, string] }
    }
}

type SafeStorageDB = IDBPDatabase<SafeStorageGameDBSchema>

// This array stores migration-like version information
const IDB_VERSIONS = [
    {
        upgrade: (db: SafeStorageDB) => {
            const store = db.createObjectStore(IDB_STORE, {
                keyPath: ['gameId', 'gameKey'],
            })
            store.createIndex('gameId', 'gameId', { unique: false })
        },
    },
]

const createDb = async () => {
    // Here we use latest available migrations from IDB_VERSIONS and depending on what's on client running neccessary migrations
    // Note: version can't be 0, so it's 1-based
    return await openDB<SafeStorageGameDBSchema>(IDB_NAME, IDB_VERSIONS.length, {
        upgrade(db, oldVersion, newVersion) {
            for (let i = oldVersion; i < (newVersion || IDB_VERSIONS.length - 1); i += 1) {
                IDB_VERSIONS[i]?.upgrade(db)
            }
        },
    })
}

export const createIdbApi = (gameHru: string) => {
    let cachedDb: SafeStorageDB | undefined
    const getDB = async () => {
        if (!cachedDb) {
            cachedDb = await createDb()
        }

        return cachedDb
    }

    const setItems = async (state: Record<string, string>) => {
        const db = await getDB()
        const transaction = db.transaction(IDB_STORE, 'readwrite')
        Object.keys(state).forEach((gameKey) => {
            transaction.store.put({
                value: state[gameKey] as string,
                gameId: gameHru,
                gameKey,
            })
        })
        return transaction.done
    }

    const getState = async () => {
        const db = await getDB()
        const keys = await db.getAllFromIndex(IDB_STORE, 'gameId', gameHru)

        return keys.reduce(
            (store, record) => {
                store[record.gameKey as string] = record.value

                return store
            },
            {} as Record<string, string>,
        )
    }

    const deleteKeys = async (keys: string[]) => {
        const db = await getDB()
        const transaction = db.transaction(IDB_STORE, 'readwrite')
        keys.forEach((key: string) => {
            transaction.store.delete([gameHru, key])
        })
        return transaction.done
    }

    return { getState, setItems, deleteKeys }
}

export const createSafeStorage = (
    gameHru: string,
    getUser: () => Promise<User | null>,
    cloudSaveApi: {
        getState: () => Promise<Record<string, string>>
        setItems: (state: Record<string, string>) => Promise<boolean>
    },
    onSave: (data: { type: 'storage'; action: MessageAction }) => void,
) => {
    let cachedCloudSaveEnabled: boolean | undefined
    const getCloudSyncEnabled = async () => {
        const user = await getUser()
        if (!user) {
            return undefined
        }

        if (cachedCloudSaveEnabled === undefined) {
            cachedCloudSaveEnabled = Object.keys(await getCloudSaveState()).length === 0
        }

        return cachedCloudSaveEnabled
    }

    const { getState, setItems, deleteKeys } = createIdbApi(gameHru)
    const { getState: getCloudSaveState, setItems: setCloudSaveState } = cloudSaveApi

    return async (event: GameEvent) => {
        // Only back up if cloud save is empty, so it would not overwrite something important
        const cloudSyncEnabled = await getCloudSyncEnabled()
        switch (event.data.action) {
            case MessageAction.getState:
                return {
                    type: 'storage',
                    action: MessageAction.initStorage,
                    originalMessageEvent: event as GameEvent,
                    payload: {
                        state: await getState(),
                    },
                } as OutcomingMessage
            case MessageAction.setItems: {
                onSave({ type: 'storage', action: MessageAction.setItems })
                await setItems((event.data.payload || {}) as Record<string, string>)

                if (cloudSyncEnabled) {
                    setCloudSaveState(await getState())
                }

                break
            }
            case MessageAction.deleteItems: {
                const keys = (event.data.payload?.keys || []) as string[]

                await deleteKeys(keys)

                if (cloudSyncEnabled) {
                    setCloudSaveState(await getState())
                }

                break
            }
            default:
        }
        return undefined
    }
}

export type SafeStorageMessageHandler = ReturnType<typeof createSafeStorage>
